import React, { useState, useEffect } from 'react';
import style from './group.module.scss';
import AddIcon from '../../../../../../../../assets/svg/solo-chat/circle-plus.svg';
import LineIcon from '../../../../../../../../assets/svg/solo-chat/line.svg';
import { TabSelector, Option } from '../../../../components';
import { Group as TGroup, Entity as TEntity } from '../../types';
import { useMutate, useAppAndBranchId, usePermissions} from '../../../../hooks';
import { api } from '../../api';
import { LoadingIcon } from '../../../../icons/LoadingIcon';
import { Title, DeleteGroup, Utterances, Entities } from './components';
import { tabs } from '../constants';
import { useCRUD } from '../../../../hooks';
import { useEntities } from './hooks';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../../utils/error';

type Props = {
	intentId: string;
	group: TGroup;
	updateGroupById: (id: string, group: TGroup) => void;
	removeGroupById: (id: string) => void;
	refetchGroups: () => void;
	entitiesData: TEntity[] | null
};

export type EditUtterance = {
	index: number;
	initialValue: string;
	currentValue: string;
};

export function Group(props: Props) {
  const { group, intentId, updateGroupById, refetchGroups, removeGroupById, entitiesData } = props
  const [appId, branchId] = useAppAndBranchId()

  const [isAddingUtterance, setIsAddingUtterance] = useState(false)
  const [isAddingEntity, setIsAddingEntity] = useState(false)
  const [editUtterance, setEditUtterance] = useState<EditUtterance>({
    index: -1,
    initialValue: "",
    currentValue: ""
  })
  const [selected, setSelected] = useState<"utterances" | "entities">("utterances")

  const groupIdTokens = group.id.split(":")
  const isLocalGroup = groupIdTokens.length === 2 && groupIdTokens[1] === "state"
  const [isEditingTitle, setIsEditingTitle] = useState(isLocalGroup)
  const { ErrorToast } = useToast()

  const { mutate: createMutate, loading: createLoading } = useMutate(api.createGroup)
  const { mutate: updateMutate, loading: updateLoading } = useMutate(api.updateGroup)
  const canCreate = usePermissions('nlu.Create')

  const utterancesCRUD = useCRUD(group.utterances?.length ?? 0)
  // CRD as this hook don't perform "Update"
  const entitiesCRD = useEntities(group.entities?.length ?? 0)

  const entitiesOptions: Option[] = entitiesData?.map((item) => {
    const options = item.entityValues?.map(el => {
      return { value: el.id, label: el.name  }
    }) ?? []
    return {
      value: item.id,
      label: item.name,
      options
    }
  }) ?? []

  async function handleSave() {
    const payload: Record<string, any> = {
      name: group.name,
      branchId
    }

    payload.utterances = group.utterances?.
      concat(utterancesCRUD.list).
      filter((_, i) => !utterancesCRUD.deleteList.includes(i)).
      map((item, i) => utterancesCRUD.updateList[i] ?? item)

    if(Object.keys(entitiesCRD.list).length) {
      payload.entities = {}    
      if (isLocalGroup) {
        payload.entities = entitiesCRD.list
      } else {
        payload.entities.create = entitiesCRD.list
      }
    }

    if(Object.keys(entitiesCRD.deleteList).length > 0) {
      if(!payload.entities) {
        payload.entities = {}
      }
      if (!isLocalGroup) {
        payload.entities.delete = entitiesCRD.deleteList
      }
    }

    try {
      if(isLocalGroup) {
        const res = await createMutate(appId, intentId, payload)
        refetchGroup(res.id)
        return
      }
      await updateMutate(appId, intentId, group.id, payload)
      refetchGroup()
    } catch(err:any) {
      const op = isLocalGroup ? 'create' : 'update'
      ErrorToast(errorMessage(op, 'data labelling group', err.code))
    }
  }

  function handleCancel() {
    clearState()
  }

  function handleAdd() {
    if(selected === "utterances") {
      setEditUtterance(prev => ({...prev, index: -1}))
      setIsAddingUtterance(true)
    } else if(selected === "entities") {
      setIsAddingEntity(true)
    }
  }

  async function handleGroupName(groupName: string) {
    if(isLocalGroup) {
      updateGroupById(group.id, { ...group, name: groupName })
      setIsEditingTitle(false)
      return
    }

    await updateMutate(appId, intentId, group.id, {
      name: groupName,
      branchId,
    })
    refetchGroup()
    setIsEditingTitle(false)
  }

  // clears local state and fetches updated group from server
  async function refetchGroup(id?: string) {
    const groups = await api.getGroups(appId, branchId, intentId)
    const updatedGroup = groups.find(item => item.id === (id ?? group.id))
    if(updatedGroup) {
      updateGroupById(group.id, updatedGroup)
      clearState()
    }
  }

  function clearState() {
    setIsAddingEntity(false)
    setIsAddingUtterance(false)
    setEditUtterance(prev => ({...prev, index: -1}))
    utterancesCRUD.actions.clear()
    entitiesCRD.actions.clear()
  }

  useEffect(() => {
    if(group.utterances) {
      utterancesCRUD.actions.setOffset(group.utterances.length)
    }
    if(group.entities) {
      entitiesCRD.actions.setOffset(group.entities.length)
    }
  }, [group])

  return (
    <div className={style.group} style={styles}>
      <Title 
        showInput={() => setIsEditingTitle(true)}
        closeInput={() => setIsEditingTitle(false)}
        onEnter={(groupName) => handleGroupName(groupName)}
        groupName={group.name}
        isEditing={isEditingTitle}
      />

      <div className={style.panel}>
        <TabSelector selected={selected} onSwitch={(value: any) => setSelected(value)} tabs={tabs} />

        {canCreate && <img src={LineIcon} alt="line" />}
        {canCreate &&
          <button className={style.add} onClick={handleAdd}>
            <img src={AddIcon} alt="add" />
            <span>Add {selected === "utterances" ? "Utterance" : "Entities"}</span>
          </button>
        }
        {(utterancesCRUD.isDirty || entitiesCRD.isDirty) && (
          <>
          <img src={LineIcon} alt="line" />
          <div className={style.saveCancel}>
            <button data-loading={createLoading || updateLoading} onClick={() => {
                if(!createLoading && !updateLoading) {
                  handleSave()
                }
              }} className={style.saveBtn}>
               <span>Save</span>
               {(createLoading || updateLoading) && <LoadingIcon width="14" height="14" stroke="#0466C8" />}
            </button>
            <button onClick={handleCancel} className={style.cancelBtn}>Cancel</button>
          </div>
          </>
        )}
      </div>

      {selected === "utterances" ? 
        <Utterances 
          isAdding={isAddingUtterance}
          editUtterance={editUtterance}
          setEditUtterance={setEditUtterance}
          closeInput={() => setIsAddingUtterance(false)} 
          crud={utterancesCRUD}
          utterances={group.utterances?.concat(utterancesCRUD.list) ?? []}
        /> : 
        <Entities 
          entitiesOptions={entitiesOptions}
          isAdding={isAddingEntity} 
          entitiesCRD={entitiesCRD} 
          groupEntities={group.entities ?? []}
        />
      }
      <DeleteGroup 
        isLocalGroup={isLocalGroup}
        refetchGroups={refetchGroups}
        removeGroupById={removeGroupById}
        groupId={group.id}
        groupName={group.name}
        intentId={intentId}
      />
    </div>
  )
}

const styles: any = {
  '--tab-sel-space': '6px 14px',
  '--select-width': '250px',
  '--select-space': '6px 10px',
  '--select-top': '12px',
};
